import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCheck, GraduationCap, AlertTriangle, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const classLabel = (c) => {
  if (c === "LKG" || c === "UKG") return c;
  const n = parseInt(c, 10);
  if (isNaN(n)) return c;
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const toneFor = (p) =>
  p < 50 ? "text-rose-600 dark:text-rose-400" : p <= 70 ? "text-sky-600 dark:text-sky-400" : "text-emerald-600 dark:text-emerald-300";
const barFor = (p) => (p < 50 ? "bg-rose-500" : p <= 70 ? "bg-sky-500" : "bg-emerald-500");

export function WeakSubjectsModal({ open, onOpenChange }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classFilter, setClassFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setClassFilter("all");
    setSubjectFilter("all");
    api.get("/admin/marks/weak-subjects")
      .then(({ data }) => setRows(data.rows || []))
      .catch((e) => { toast.error(apiErr(e.response?.data?.detail) || "Could not load subject analytics"); setRows([]); })
      .finally(() => setLoading(false));
  }, [open]);

  const classes = useMemo(() => {
    if (!rows) return [];
    return [...new Set(rows.map((r) => r.class_name))].sort((a, b) => {
      const na = parseInt(a, 10), nb = parseInt(b, 10);
      if (isNaN(na) && isNaN(nb)) return a.localeCompare(b);
      if (isNaN(na)) return -1;
      if (isNaN(nb)) return 1;
      return na - nb;
    });
  }, [rows]);

  const subjects = useMemo(() => {
    if (!rows) return [];
    return [...new Set(rows.map((r) => r.subject))].sort((a, b) => String(a).localeCompare(String(b)));
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    return rows.filter((r) =>
      (classFilter === "all" || r.class_name === classFilter) &&
      (subjectFilter === "all" || r.subject === subjectFilter)
    );
  }, [rows, classFilter, subjectFilter]);

  const subjectAvg = useMemo(() => {
    if (subjectFilter === "all" || filtered.length === 0) return null;
    const sum = filtered.reduce((a, r) => a + r.avg_pct, 0);
    return Math.round((sum / filtered.length) * 10) / 10;
  }, [filtered, subjectFilter]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="weak-subjects-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <span className="h-8 w-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 flex items-center justify-center">
              <BarChart3 className="h-4 w-4" />
            </span>
            Lowest-scoring subjects
          </DialogTitle>
          <DialogDescription>
            Weakest subject averages across every class (latest exam) with the subject &amp; class teacher.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-slate-400" data-testid="weak-subjects-count">
            {loading ? "Loading…" : (
              subjectAvg != null
                ? <span data-testid="weak-subjects-subject-avg"><b className="text-slate-600 dark:text-slate-300">{subjectFilter}</b> average across {filtered.length} class{filtered.length === 1 ? "" : "es"}: <b className="text-indigo-600 dark:text-indigo-300">{subjectAvg}%</b></span>
                : `${filtered.length} subject${filtered.length === 1 ? "" : "s"} · weakest first`
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="w-40">
              <Select value={subjectFilter} onValueChange={setSubjectFilter}>
                <SelectTrigger data-testid="weak-subjects-subject-filter"><SelectValue placeholder="All subjects" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All subjects</SelectItem>
                  {subjects.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Select value={classFilter} onValueChange={setClassFilter}>
                <SelectTrigger data-testid="weak-subjects-class-filter"><SelectValue placeholder="All classes" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All classes</SelectItem>
                  {classes.map((c) => (
                    <SelectItem key={c} value={c}>{classLabel(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex-1 flex flex-col min-h-0">
          <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-[0.15em] text-slate-400 shrink-0">
            <div className="col-span-2">Class</div>
            <div className="col-span-3">Subject</div>
            <div className="col-span-3">Average</div>
            <div className="col-span-2">Subject teacher</div>
            <div className="col-span-2">Class teacher</div>
          </div>

          {loading ? (
            <div className="p-10 text-center text-slate-500 text-sm">Loading subject analytics…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm" data-testid="weak-subjects-empty">
              No published exam results yet.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800 overflow-y-auto">
              {filtered.map((r, i) => (
                <li
                  key={`${r.class_name}-${r.section}-${r.subject}-${i}`}
                  className="grid grid-cols-2 sm:grid-cols-12 gap-2 sm:gap-3 px-4 py-3 items-center"
                  data-testid={`weak-subject-row-${i}`}
                >
                  <div className="sm:col-span-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {classLabel(r.class_name)}-{r.section}
                  </div>
                  <div className="sm:col-span-3 text-sm text-slate-700 dark:text-slate-200 truncate" title={r.subject}>
                    {r.subject}
                  </div>
                  <div className="col-span-2 sm:col-span-3 flex items-center gap-2">
                    <div className="flex-1 h-2.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                      <div className={`h-full rounded-full ${barFor(r.avg_pct)}`} style={{ width: `${Math.min(100, r.avg_pct)}%` }} />
                    </div>
                    <span className={`w-12 text-right text-sm font-bold ${toneFor(r.avg_pct)}`} data-testid={`weak-subject-pct-${i}`}>
                      {r.avg_pct}%
                    </span>
                  </div>
                  <div className="sm:col-span-2 text-xs text-slate-500 dark:text-slate-400 truncate inline-flex items-center gap-1" title={r.teacher || ""}>
                    <UserCheck className="h-3.5 w-3.5 shrink-0 text-slate-300" /> {r.teacher || "—"}
                  </div>
                  <div className="sm:col-span-2 text-xs text-slate-500 dark:text-slate-400 truncate inline-flex items-center gap-1" title={r.class_teacher || ""}>
                    <GraduationCap className="h-3.5 w-3.5 shrink-0 text-slate-300" /> {r.class_teacher || "—"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {!loading && filtered.some((r) => r.avg_pct < 50) && (
          <div className="flex items-center gap-2 text-xs text-rose-600 dark:text-rose-400 shrink-0">
            <AlertTriangle className="h-3.5 w-3.5" /> Subjects below 50% are failing the class average — worth a follow-up.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
